import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import React, { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { get } from "api";
import { GetResult, Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import ChevronRightSmall from "atoms/icon/icons/chevron_right_small.svg";
import CalendarIcon from "atoms/icon/icons/ic_calendar.svg";
import MembershipsIcon from "atoms/icon/icons/ic_memberships.svg";
import OffersIcon from "atoms/icon/icons/offers_nav.svg";
import RewardsIcon from "atoms/icon/icons/rewards_nav.svg";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { AnimatedBackground } from "components/AnimatedBackground";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { useFeatureFlag } from "services/Flags";
import { getMembershipsWithDiscountInformation } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";
import {
  formatBillingPeriodInterval,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";

export interface MembershipsPromoProps
  extends Models<"ShopMembershipFeatureResult"> {
  onClick?: () => void;
  "data-testid"?: string;
  memberships: GetResult<"/memberships">;
  useAnimatedBackground?: boolean;
  fullscreen?: boolean;
}

const IconMap: { [key: string]: React.FunctionComponent } = {
  offers: OffersIcon,
  rewards: RewardsIcon,
  calendar: CalendarIcon,
};

const MembershipCardList = (props: {
  memberships: Models<"MembershipModel">[];
}) => {
  const { data: shopDiscounts } = useQuery({
    queryKey: ["/shop/discounts"],
    queryFn: () => get("/shop/discounts"),
    staleTime: 1000 * 10,
  });
  const { data: customShopCategories } = useQuery({
    queryKey: ["/shop/custom-categories/listings"],
    queryFn: () => get("/shop/custom-categories/listings"),
    staleTime: 1000 * 10,
  });

  return (
    <div className="w-full">
      <ul
        className={cx(
          "flex px-4 pb-6 pt-4",
          props.memberships.length > 2
            ? "-mx-4 gap-4 overflow-x-scroll"
            : "-mx-1 gap-6"
        )}
      >
        {getMembershipsWithDiscountInformation(
          props.memberships,
          shopDiscounts,
          customShopCategories
        ).map((m) => (
          <li key={m.id}>
            <MembershipCard
              key={m.id}
              membership={m}
              discount={m.discountText}
              customColor={m.customColor}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

const renderPrice = (
  isGuest: boolean,
  monthlyCost: number,
  isFlexibleMembershipsEnabled: boolean,
  billingPeriodCost?: number | null,
  billingPeriodIntervalCount?: number | null,
  parsedBillingPeriodInterval?: string | null
) => {
  if (isGuest) {
    return "See price";
  }
  return (
    <>
      {isFlexibleMembershipsEnabled ? (
        <span>
          {formatBillingPeriodInterval(
            billingPeriodCost ?? monthlyCost,
            parsedBillingPeriodInterval ?? "month",
            billingPeriodIntervalCount ?? 1
          )}
        </span>
      ) : (
        <>
          <span className="text-bold1">{formatCurrency(monthlyCost)}</span>
          <span className="text-body1">/month</span>
        </>
      )}
    </>
  );
};

const getLink = (membershipId: string) => {
  return `/pdp/membership/${membershipId}`;
};

const MembershipCardLarge = (props: Models<"MembershipModel">) => {
  const navigate = useNavigate();
  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchMembershipPDP } = usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting) return;
    prefetchMembershipPDP(props.id);
  }, [isIntersecting, props.id, prefetchMembershipPDP]);

  const isGuest = user?.isGuest || false;
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const parsedBillingPeriodInterval = parseBillingPeriodName(
    props.billingPeriodInterval
  );

  return (
    <div
      ref={ref}
      className="my-4 flex h-[263px] w-[342px] cursor-pointer flex-col overflow-hidden rounded-lg bg-cover shadow-md backdrop-blur-sm transition-shadow active:scale-[0.98]"
      onClick={() => navigate(getLink(props.id))}
    >
      <div className="relative">
        <img
          className="h-[190px] w-full object-cover"
          src={props.imageUrl ?? ""}
        />
        {/* Transparent gradient fade from image to card */}
        <div className="absolute bottom-0 h-8 w-full bg-gradient-to-b from-transparent to-white/80" />
      </div>
      <div className="h-[73px] bg-one py-3 pl-6 pr-2 text-primary">
        <div className="line-clamp-1 font-serif text-h2">{props.name}</div>
        <div className="bottom-0 flex items-center justify-between rounded-b">
          <div className="text-body2">
            {renderPrice(
              isGuest,
              props.monthlyCost,
              isFlexibleMembershipsEnabled,
              props.billingPeriodCost,
              props.billingPeriodIntervalCount,
              parsedBillingPeriodInterval
            )}
          </div>
          <Icon svg={ChevronRight} color="brandColor" size="x2small" />
        </div>
      </div>
    </div>
  );
};

const MembershipCard = (props: {
  membership: Models<"MembershipModel">;
  /** Max discount for Product */
  discount?: string | null;
  /** Custom color for tag (site wide promo) */
  customColor?: string | null;
}) => {
  const navigate = useNavigate();
  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchMembershipPDP } = usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting) return;
    prefetchMembershipPDP(props.membership.id);
  }, [isIntersecting, props.membership.id, prefetchMembershipPDP]);

  const isGuest = user?.isGuest || false;
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const parsedBillingPeriodInterval = parseBillingPeriodName(
    props.membership.billingPeriodInterval
  );

  return (
    <div
      ref={ref}
      className="relative flex h-[225px] w-[156px] cursor-pointer flex-col overflow-hidden rounded-lg bg-cover shadow-md backdrop-blur-sm transition-shadow active:scale-[0.98]"
      onClick={() => navigate(getLink(props.membership.id))}
    >
      <div className="relative">
        {props.discount && (
          <div className="absolute left-2 top-2 z-10">
            <DescriptiveTag
              color={props.customColor ? "custom" : "primary"}
              size="small"
              customColor={props.customColor}
            >
              {props.discount}
            </DescriptiveTag>
          </div>
        )}
        <img
          className="h-[124px] w-full object-cover"
          src={props.membership.imageUrl ?? ""}
        />
        {/* Transparent gradient fade from image to card */}
        <div className="absolute bottom-0 h-8 w-full bg-gradient-to-b from-transparent to-white/80" />
      </div>
      <div className="flex h-[101px] flex-col justify-between bg-one p-3 text-primary">
        <div className="line-clamp-2 font-serif text-h3">
          {props.membership.name}
        </div>
        <div className="bottom-0 flex items-center justify-between rounded-b">
          <div className="text-body2">
            {renderPrice(
              isGuest,
              props.membership.monthlyCost,
              isFlexibleMembershipsEnabled,
              props.membership.billingPeriodCost,
              props.membership.billingPeriodIntervalCount,
              parsedBillingPeriodInterval
            )}
          </div>
          <Icon svg={ChevronRightSmall} color="brandColor" size="x2small" />
        </div>
      </div>
    </div>
  );
};

const MembershipPerksList = (props: {
  perks: Models<"ShopMembershipFeaturePerkResult">[];
}) => {
  return (
    <ul>
      {props.perks.map((p) => (
        <MembershipPerkItem key={p.id} {...p} />
      ))}
    </ul>
  );
};

const MembershipPerkItem = (
  props: Models<"ShopMembershipFeaturePerkResult">
) => {
  const svg = (props.icon && IconMap[props.icon]) || OffersIcon;
  return (
    <li className="flex flex-row items-center pt-3.5">
      <div className="min-w-6">
        <Icon color="white" svg={svg} size="xsmall" />
      </div>
      <div className="pl-4">{props.perk}</div>
    </li>
  );
};

const MembershipPromo = (props: MembershipsPromoProps) => {
  const memberships = props.memberships;
  const membershipsCount = props.memberships.length;
  const useAnimatedBackground = props.useAnimatedBackground ?? false;
  const fullscreen = props.fullscreen ?? false;

  const { data: userMemberships } = useQuery({
    queryKey: ["/platform-user-memberships"],
    queryFn: () => get("/platform-user-memberships"),
    staleTime: 1000 * 60 * 2,
  });

  const activeMemberships =
    userMemberships?.filter((membership) => !membership.canceled) || [];

  const isUserMember = activeMemberships.length > 0;

  const formatMembershipNames = (memberships: any[]) => {
    if (memberships.length === 0) return "";
    if (memberships.length === 1) return memberships[0].membership.name;
    if (memberships.length === 2) {
      return `${memberships[0].membership.name} and ${memberships[1].membership.name}`;
    }

    const names = memberships.map((m) => m.membership.name);
    const lastMembership = names.pop();
    return `${names.join(", ")}, and ${lastMembership}`;
  };

  const getTitle = () => {
    if (!isUserMember) return props.title;

    const membershipNames = formatMembershipNames(activeMemberships);
    const words = membershipNames.trim().split(" ");
    const endsWithMember = words.at(-1)?.toLowerCase().startsWith("member");

    const base = endsWithMember
      ? words.slice(0, -1).join(" ")
      : membershipNames;

    return `Thanks for being a ${base} member!`;
  };

  const content = (
    <div
      className={`flex flex-col items-center px-4 py-6 text-white ${
        !useAnimatedBackground ? "bg-brand-color" : ""
      }`}
    >
      <div className="pb-4">
        <Icon color="white" svg={MembershipsIcon} size="xsmall" />
      </div>
      <div className="text-center font-serif text-h2">{getTitle()}</div>
      {memberships && membershipsCount == 1 && (
        <MembershipCardLarge {...memberships[0]} />
      )}
      {memberships && membershipsCount > 1 && (
        <MembershipCardList memberships={memberships ?? []} />
      )}
      <div className="pb-0.5 pt-1 text-center text-sub1 uppercase">
        {props.perksTitle}
      </div>
      <div className="flex w-full justify-center px-4 pb-8 text-body1">
        <MembershipPerksList perks={props.perks?.slice(0, 4) ?? []} />
      </div>
      <div className="flex w-full flex-col gap-3 px-2">
        <NavLink to="/shop/memberships">
          <Button fullWidth size="large" style="outline">
            {membershipsCount == 1
              ? "View all perks and join now!"
              : membershipsCount == 2
              ? "Shop our memberships"
              : `Shop all ${membershipsCount} memberships`}
          </Button>
        </NavLink>
        {isUserMember && (
          <NavLink to="/wallet/memberships">
            <Button fullWidth size="large" style="white">
              View your benefits
            </Button>
          </NavLink>
        )}
      </div>
    </div>
  );

  return useAnimatedBackground ? (
    <AnimatedBackground
      mode="dark"
      className={fullscreen ? "min-h-screen" : ""}
    >
      {content}
    </AnimatedBackground>
  ) : (
    content
  );
};

export default MembershipPromo;
